<script lang="ts" setup>
  import { computed, onMounted, ref } from 'vue'
  import { jwtDecode } from 'jwt-decode'
  import { TransitionChild, TransitionRoot } from '@headlessui/vue'
  import NavigationShared from './NavigationShared.vue'
  import LogoDefault from './LogoDefault.vue'
  import { useAuthStore, useGeneratedRoleStore } from '@/stores'
  import { keyBy } from 'lodash-es'
  import type { Role } from '@/api'
  import Dialog from 'primevue/dialog'
  import ButtonDefault from '@/elements/ButtonDefault.vue'
  import { useAppState } from '@/composables/useAppState.ts'
  import { useLocalStorage } from '@vueuse/core'
  import { useFormatter } from '@/composables/useFormatter.ts'
  import { DateTime } from 'luxon'

  const roles = ref<Record<string, Role>>()
  onMounted(async () => {
    roles.value = keyBy(
      await useGeneratedRoleStore()
        .findAll(null)
        .then((d) => d.value),
      (r: Role) => r.internal
    ) as Record<string, Role>
  })

  const isVisible = useLocalStorage<boolean>('navigation-visible', true, { writeDefaults: true })

  defineProps({
    navigation: Array
  })
  let version = __BUILD_VERSION__
  if (process.env.NODE_ENV === 'development') version += '-dev'
  const apiHash = __API_HASH__
  const version2 = useFormatter().DateTime(__BUILD_DATE__)
  const backendUrl = ref(useAppState().backendUrl.value)
  const tokenTtl = computed(() => {
    const { token } = useAuthStore()
    if (!token) return 'undef'
    const { exp } = jwtDecode(token)
    if (!exp) return 'undef'
    return DateTime.fromSeconds(exp).toISOTime({ suppressMilliseconds: true, includeOffset: false })
  })

  const [CI, CI_COMMIT_BRANCH, CI_COMMIT_SHA, CI_COMMIT_SHORT_SHA, CI_PIPELINE_ID] = [__CI__, __CI_COMMIT_BRANCH__, __CI_COMMIT_SHA__, __CI_COMMIT_SHORT_SHA__, __CI_PIPELINE_ID__]

  const newBackendDialogVisible = ref(false)

  function openNewBackendInput() {
    newBackendDialogVisible.value = true
  }

  async function changeBackend() {
    await useAppState().setBackend(backendUrl.value)
  }
</script>
<template>
  <div>
    <!--    Desktop sidebar    -->
    <TransitionRoot
      :show="isVisible"
      as="template">
      <TransitionChild
        as="template"
        enter="transition ease-in-out duration-300 transform"
        enter-from="-translate-x-60"
        enter-to="translate-x-0"
        leave="transition ease-in-out duration-300 transform"
        leave-from="translate-x-0"
        leave-to="-translate-x-60">
        <div class="hidden sm:block inset-y-0 w-72 flex-col md:sticky md:top-0">
          <div class="flex grow flex-col gap-y-5 border-r border-gray-200 dark:border-gray-800 px-6">
            <RouterLink
              :to="$router.resolve('/')"
              active-class=""
              class="flex h-16 shrink-0 items-center"
              exact-active-class="">
              <LogoDefault />
            </RouterLink>
            <nav class="flex flex-1 flex-col">
              <NavigationShared :mobile="false" />

              <div class="mt-4 text-xs text-right text-gray-300">
                <span>Version: {{ version }}</span>
                <br />
                <span>Build: {{ version2 }}</span>
                <br />
                <span>Token-TTL: {{ tokenTtl }}</span>
                <br />
                <span>API: {{ apiHash }}</span>
                <br />

                <div v-if="$titrop.user.roles.developer && roles">
                  <hr />
                  <span @dblclick="openNewBackendInput">Backend: {{ backendUrl }}</span>
                  <hr />
                  Ihre Berechtigungsrollen:
                  <ul>
                    <li
                      v-for="role in $titrop.user.roles.AllRoles"
                      :key="role">
                      <span :title="roles[role]?.description ?? '(keine Beschreibung)'">{{ roles[role]?.name ?? role }}</span>
                    </li>
                  </ul>
                  <div v-if="CI">
                    <hr />
                    CI-Meta:
                    <br />
                    1:{{ CI }}
                    <br />
                    2:{{ CI_COMMIT_BRANCH }}
                    <br />

                    <a :href="`https://aplgit01.inceptum.lan/businessone/webapps/titrop-frontend/-/tree/${CI_COMMIT_SHA}`">{{ CI_COMMIT_SHORT_SHA }}</a>
                    <br />
                    4:
                    <br />
                    5:{{ CI_PIPELINE_ID }}
                  </div>
                </div>
              </div>
            </nav>
          </div>
        </div>
      </TransitionChild>
    </TransitionRoot>
    <Dialog
      v-model:visible="newBackendDialogVisible"
      header="Select backend"
      modal>
      <form @submit.prevent="changeBackend">
        <Dropdown
          v-model="backendUrl"
          :options="['https://dev.titrop.com', 'https://staging.titrop.com', 'https://titrop.com']" />
        <ButtonDefault type="submit">Save</ButtonDefault>
      </form>
    </Dialog>
  </div>
</template>
